import { DestroyRef as SearchDestroyRef, inject as searchInject } from '@angular/core';
import { bindSearchModeForm } from '../../models/search-mode-form';
import { selectCarState } from '../../store/selectors/car.selectors';
import { ensureCarList } from '../../store/actions/car.actions';
import { resetCarSearch } from '../../store/actions/car.actions';
import { withoutViewport } from '../../models/search-request';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Car } from '../../models/car-model';
import { CarSearch } from '../../models/car-search.model';
import { filter, map, Observable, Subject, take, takeUntil } from 'rxjs';
import { AdCategories } from '../../models/ad-categories.model';
import { Store } from '@ngrx/store';
import * as CategoriesActions from '../../store/actions/categories.actions';
import * as CategoriesSelectors from '../../store/selectors/categories.selectors';
import {
  selectCarSearchForm,
  selectCarSearchLoading,
  selectCarSearchResults,
  selectIsCarMapView,
} from '../../store/selectors/car.selectors';
import { searchCars, searchMapCars, updateSearchForm } from '../../store/actions/car.actions';
import { selectIsArabic } from '../../store/selectors/language.selectors';
import { Roles } from '../../shared/constants/role.constants';
import { NgSelectComponent } from '@ng-select/ng-select';
import { Location } from '@angular/common';
import { MapParameters } from '../../models/map-parameters.model';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-car-search',
  standalone: false,
  templateUrl: './car-search.component.html',
  styleUrl: './car-search.component.scss',
})
export class CarSearchComponent implements OnInit, OnDestroy {
  private readonly searchDestroyRef = searchInject(SearchDestroyRef);
  searchForm!: FormGroup;
  cars: Car[] = [];
  loadingResult = false;
  advancedVisible = false;
  maxYear = new Date().getFullYear() + 1;

  isArabic$: Observable<boolean> = this.store.select(selectIsArabic);

  categories$!: Observable<AdCategories | null>;
  loadingCategories$!: Observable<boolean>;

  searchResults$ = this.store.select(selectCarSearchResults);
  loadingResult$ = this.store.select(selectCarSearchLoading);
  isMapView = toSignal(this.store.select(selectIsCarMapView));

  mapParameters?: MapParameters;

  roles = Roles;

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private store: Store,
    private location: Location
  ) {
    window.scrollTo(0, 0);
    this.categories$ = this.store.select(CategoriesSelectors.selectCategories).pipe(
      filter((res) => !!res),
      map((res) => {
        return {
          ...res,
          statuses: [...res?.statuses, { code: 'EXPIRED', description: 'EXPIRED' }],
        } as AdCategories;
      })
    );
    this.loadingCategories$ = this.store.select(CategoriesSelectors.selectCategoriesLoading);

    this.store.dispatch(CategoriesActions.loadCategories());
    this.store.dispatch(ensureCarList());

  }

  ngOnInit(): void {
    this.searchForm = this.fb.group({
      name: '',
      isForRent: undefined,
      models: [],
      bodyTypes: [],
      carUseTypes: [],
      driveTypes: [],
      seatCovers: [],
      fuelTypes: [],
      seatFrom: undefined,
      seatTo: undefined,
      doorFrom: undefined,
      doorTo: undefined,
      weightFrom: undefined,
      weightTo: undefined,
      cylinderFrom: undefined,
      cylinderTo: undefined,
      performanceFrom: undefined,
      performanceTo: undefined,
      kilometersFrom: undefined,
      kilometersTo: undefined,
      yearFrom: undefined,
      yearTo: undefined,
      isInstallmentAvailable: this.fb.control<boolean | null>(null),
      onlyWithPrice: this.fb.control(false, { nonNullable: true }),
      priceFrom: undefined,
      priceTo: undefined,
      conditions: [],
      withWarranty: undefined,
      isAccident: undefined,
      isManulaGear: undefined,
      hasSunroof: undefined,
      hasPanoramicSunroof: undefined,
      colors: [],
      interiorColors: [],
      statuses: [],
      hasFrontCamera: undefined,
      hasRearCamera: undefined,
      hasCentralLock: undefined,
      hasKeylessEntry: undefined,
      hasPushButtonStart: undefined,
      hasSportSeats: undefined,
      hasCruiseControl: undefined,
      hasAbs: undefined,
      hasAlarmSystem: undefined,
      hasSeatHeating: undefined,
      hasElectricSeats: undefined,
      hasMemorySeats: undefined,
      hasMassageSeats: undefined,
      hasVentilatedSeats: undefined,
      hasSteeringWheelHeating: undefined,
      hasElectricWindows: undefined,
      hasElectricMirrors: undefined,
      hasElectricTailgate: undefined,
      hasReserveTire: undefined,
      hasWirelessCharging: undefined,
      hasInfotainmentScreen: undefined,
      has220VoltOutlet: undefined,
      has360Camera: undefined,
      hasFrontSensor: undefined,
      hasRearSensor: undefined,
      northEastLat: undefined,
      northEastLng: undefined,
      southWestLat: undefined,
      southWestLng: undefined,
    });

    bindSearchModeForm(this.searchForm, this.store.select(selectCarState),
      state => state.isCarMapView, form => this.store.dispatch(updateSearchForm({form})), this.searchDestroyRef);
  }

  onSearch() {
    const searchParams = (this.searchForm?.value as CarSearch) ?? ({} as CarSearch);
    this.store.dispatch(updateSearchForm({ form: searchParams }));
    this.callSearch(searchParams);
  }

  callSearch(searchParams: CarSearch) {
    const isMapView = this.isMapView();

    if (isMapView) {
      this.store.dispatch(searchMapCars({ mapQuery: { ...searchParams, isMapView } }));
    } else {
      this.clearMapData();
      this.store.dispatch(searchCars({ query: withoutViewport(searchParams) }));
    }
  }

  reset() {
    this.searchForm.reset({ onlyWithPrice: false });
    if (this.isMapView()) this.mapParameters = undefined;
    this.clearMapData();
    this.store.dispatch(resetCarSearch());
  }

  mapChanged(event: MapParameters) {
    this.mapParameters = event;
    this.northEastLat?.setValue(event.northEastLat);
    this.northEastLng?.setValue(event.northEastLng);
    this.southWestLat?.setValue(event.southWestLat);
    this.southWestLng?.setValue(event.southWestLng);
    this.onSearch();
  }

  clearMapData() {
    this.northEastLat?.setValue(undefined);
    this.northEastLng?.setValue(undefined);
    this.southWestLat?.setValue(undefined);
    this.southWestLng?.setValue(undefined);
  }

  toggleAdvanced(): void {
    this.advancedVisible = !this.advancedVisible;
    if (!this.advancedVisible) {
      window.scrollTo(0, 0);
    }
  }

  toggleIsRent(value: boolean): void {
    const isForRent = this.searchForm.get('isForRent');
    if (
      (isForRent?.value === true && value === true) ||
      (isForRent?.value === false && value === false)
    ) {
      isForRent?.setValue(undefined);
    } else {
      isForRent?.setValue(value);
    }
  }

  onSelectionChange(selected: any[], select: NgSelectComponent) {
    if (selected.length >= 5) {
      select.close(); // Hides the dropdown menu
    }
  }

  goBack() {
    this.location.back();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get usedCriteriaCount(): number {
    return Object.entries(this.searchForm.value).filter(([key, v]) => key === 'onlyWithPrice' ? v === true : this.isUsed(v)).length;
  }

  isUsed(value: any): boolean {
    if (typeof value === 'boolean') {
      return value !== undefined;
    }

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    if (typeof value === 'object' && value !== null) {
      return Object.values(value).some((v) => this.isUsed(v));
    }

    return value !== null && value !== undefined && value !== '' && !/^\d+\.\d+/.test(value);
  }

  get northEastLat() {
    return this.searchForm?.get('northEastLat');
  }
  get northEastLng() {
    return this.searchForm?.get('northEastLng');
  }
  get southWestLat() {
    return this.searchForm?.get('southWestLat');
  }
  get southWestLng() {
    return this.searchForm?.get('southWestLng');
  }
}
